import React, { useEffect, useRef, useState } from "react"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { FetchBaseQueryError } from "@reduxjs/toolkit/query"
import useEventListener from "hooks/useEventListener"
import { Role } from "types/general.types"
import { getCurrentPathInfo } from "utils/navigatorUtils"
import { getUnseenCount, resetUnseenCount } from "utils/indexedDB"
import { OWNER, VIEWER, REJECTED, FETCH_ERROR } from "constants/index"
import { EVENTS_ROUTES_MAP } from "constants/eventsRoutesMap"
import { useAuth } from "context/AuthContext"
import { useAppSelector } from "appRedux/hooks"
import {
  NAVIGATE_TO_SPECIFIC_PATH,
  NAVIGATE_TO_CREATE_ITEM_PAGE,
  NAVIGATE_TO_LOGIN_PAGE,
  NAVIGATE_TO_SETTINGS_PAGE,
  NAVIGATE_TO_FEED_PAGE,
  NAVIGATE_TO_PROFILE_PAGE,
  NAVIGATE_TO_PREMIUM_PLANS_PAGE,
  NAVIGATE_TO_CREATE_LIST_PAGE
} from "constants/events"
import { resetViewedUserProfileSlice, updateViewedUserProfile } from "appRedux/slices/viewedUserSlice"
import { resetProfileTabsSlice } from "appRedux/slices/profileTabsSlice"
import { useInitQuery } from "appRedux/slices/initApiSlice"
import {
  updateActivePremiumFeatures,
  updatePremiumSlidesForActiveFeatures,
  updateShowPremiumOnboarding
} from "appRedux/slices/premiumSlice"
import {
  updateCurrentPageInfo,
  updateCurrentPath,
  updatePreviousPageInfo,
  updatePreviousPath, updateRole
} from "appRedux/slices/globalSlice"
import { useNotification } from "hooks/usePushSubscription"
import useMetaTags from "hooks/useMetaTags"
import ErrorBoundary from "components/templates/ErrorBoundary"
import SplashScreen from "components/templates/SplashScreen"
import ErrorTemplate from "components/templates/ErrorTemplate"

export default function RootLayout() {
  const [isValidatingSession, setIsValidatingSession] = useState<boolean>(true)

  const hasSubscribeAttemptedRef = useRef(false)

  const currentPath = useAppSelector((state) => state.global.currentPath)
  const currentPageInfo = useAppSelector((state) => state.global.currentPageInfo)
  const userProfile = useAppSelector((state) => state.user.profile)
  const viewedUser = useAppSelector((state) => state.viewedUser)
  const showPremiumOnboarding = useAppSelector((state) => state.premium.showPremiumOnboarding)

  const initQueryResult = useInitQuery()

  const { user, isAuthenticated } = useAuth()
  const { handleSubscribe } = useNotification()
  useMetaTags()

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const pathname = location.pathname
  const { t } = useTranslation()

  useEventListener(document, "click", handleDocumentClicked)

  useEventListener(document, NAVIGATE_TO_LOGIN_PAGE, handleNavigation)

  useEventListener(document, NAVIGATE_TO_SETTINGS_PAGE, handleNavigation)

  useEventListener(document, NAVIGATE_TO_FEED_PAGE, handleNavigation)

  useEventListener(document, NAVIGATE_TO_CREATE_ITEM_PAGE, handleNavigation)

  useEventListener(document, NAVIGATE_TO_CREATE_LIST_PAGE, handleNavigation)

  useEventListener(document, NAVIGATE_TO_SPECIFIC_PATH, handleNavigateToSpecificPath)

  useEventListener(document, NAVIGATE_TO_PROFILE_PAGE, handleNavigateToProfilePage)

  useEventListener(document, NAVIGATE_TO_PREMIUM_PLANS_PAGE, handleNavigation)

  useEffect(() => {
    document.addEventListener("click", handleDocumentClicked)
    handleClearAppBadge()
  }, [])

  useEffect(() => {
    if (isAuthenticated && pathname.startsWith("/public")) {
      navigate(`${pathname.replace("/public", "")}${location.search}`, { replace: true })
      return
    }
    updateStatesBasedOnPathChange(pathname)
    if (showPremiumOnboarding) {
      dispatch(updateShowPremiumOnboarding(false))
    }
  }, [pathname, isAuthenticated])

  useEffect(() => {
    dispatch(updateActivePremiumFeatures())
    dispatch(updatePremiumSlidesForActiveFeatures())
    if (initQueryResult.status === REJECTED && (initQueryResult?.error as FetchBaseQueryError)?.status === FETCH_ERROR) return
    if (initQueryResult.isSuccess || initQueryResult.isError) {
      setIsValidatingSession(false)
    } else {
      setIsValidatingSession(true)
    }
  }, [initQueryResult])

  useEffect(() => {
    if (userProfile?.handle === viewedUser?.profile?.handle) {
      dispatch(updateViewedUserProfile(userProfile))
    }
  }, [JSON.stringify(userProfile), userProfile?.avatar])

  async function handleClearAppBadge() {
    const unseenNotificationsCount = await getUnseenCount()
    if (unseenNotificationsCount === 0) return
    if ("serviceWorker" in navigator && navigator?.clearAppBadge) {
      navigator.clearAppBadge()
        .then(() => console.debug("App badge cleared."))
        .catch((error: any) => console.error(`Error clearing app badge. Error: ${error}`))
    }
    await resetUnseenCount()
  }

  function updateStatesBasedOnPathChange(pathname: string) {
    dispatch(updatePreviousPath(currentPath))
    dispatch(updateCurrentPath(pathname))
    dispatch(updatePreviousPageInfo(currentPageInfo))

    const pageInfo = updatePageInfo(pathname)
    if (pageInfo) {
      const isOwner = getRole(pageInfo?.dynamicPathParts?.handle, user?.handle, pageInfo?.path)
      const hasBackButton = (isOwner && pageInfo?.showInNavigation) ? false : pageInfo?.hasBackButton
      const showInNavigation = isOwner ? pageInfo?.showInNavigation : false
      dispatch(updateCurrentPageInfo({ ...pageInfo, hasBackButton, showInNavigation }))
      dispatch(updateRole((isOwner ? OWNER : VIEWER) as Role))
      if (pageInfo?.dynamicPathParts?.handle !== currentPageInfo?.dynamicPathParts?.handle) {
        dispatch(resetViewedUserProfileSlice())
        dispatch(resetProfileTabsSlice())
      }
    }
  }

  function updatePageInfo(pathname: string) {
    const pathInfo = getCurrentPathInfo(pathname)
    if (!pathInfo) {
      console.error(`pathInfo not found. pathname: ${pathname}`)
      return
    }
    return {
      ...pathInfo,
      title: pathInfo.title ? t(pathInfo.title) : ""
    }
  }

  function getRole(dynamicHandleFromPath?: string, loggedInUserHandle?: string, currentPath?: string) {
    if (currentPath?.includes("/:handle")) {
      return dynamicHandleFromPath === loggedInUserHandle
    } else {
      return true
    }
  }

  function handleDocumentClicked() {
    if (pathname.startsWith("/login") || pathname.startsWith("/register")) return
    if (hasSubscribeAttemptedRef.current) return
    handleSubscribe().then((isDone: boolean) => {
      if (isDone) {
        hasSubscribeAttemptedRef.current = true
      }
    })
  }

  function handleNavigation(event: CustomEvent) {
    const eventName = event.type as keyof typeof EVENTS_ROUTES_MAP
    let targetPath = EVENTS_ROUTES_MAP[eventName]
    const eventDetail = event.detail
    if (eventDetail?.referer && eventDetail?.referer !== "/login" && eventDetail?.referer !== "/register") {
      targetPath += `?referer=${eventDetail?.referer}`
    }
    navigate(targetPath)
  }

  function handleNavigateToSpecificPath(event: CustomEvent) {
    const eventDetail = event.detail
    if (eventDetail.targetPath) {
      navigate(eventDetail.targetPath)
    }
  }

  function handleNavigateToProfilePage() {
    if (!userProfile?.handle) return
    navigate(`/user/${userProfile?.handle}`)
  }

  return (
    isValidatingSession ? <SplashScreen/> :
      <ErrorBoundary fallback={<ErrorTemplate/>}>
        <Outlet/>
      </ErrorBoundary>
  )
}
